import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { useAuth } from '../../../global/contexts/auth/auth';
import { PAGE_SIZE } from '../utils/operator';
import { coordinationKeys } from '../queries/keys';
import { coordinationPaths } from '../queries/paths';
import { FIXTURE_SCOPE, SOURCE, onFixtures, readSample } from '../queries/source';

// TSS vite/03 — server state for the coordination demo.
//
// ONE SHAPE, TWO ORIGINS. Every hook answers `{ data, isLoading, error, origin }`
// where `origin` says which fixture sample (or which request) produced what is
// on screen. The screens print it: a demo whose numbers cannot be traced back
// to a route is exactly the "made-up data that looks real" nobody asked for.
//
// A fixture answers for ONE scope — one seat, one task, one filter. Asking for
// another one is answered with `unavailable`, never with the sample shown under
// somebody else's name.

const FIXTURE_STALE = Infinity;

function fixtureQuery(queryKey, name, guard) {
    return {
        queryKey,
        queryFn: () => {
            const missing = guard?.();
            if (missing) return { unavailable: missing };
            const { response, request, file, sample } = readSample(name);
            return { data: response, origin: { kind: 'fixture', file, sample, request } };
        },
        staleTime: FIXTURE_STALE,
    };
}

function apiQuery(queryKey, path, params) {
    return {
        queryKey,
        refetchInterval: 15_000,
        refetchIntervalInBackground: false,
        queryFn: ({ signal }) =>
            api.get(path, { params, signal }).then(r => ({
                data: r.data,
                origin: { kind: 'api', request: requestLine(path, params) },
            })),
    };
}

function requestLine(path, params) {
    const query = new URLSearchParams(
        Object.entries(params ?? {}).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return `GET /api/v1/${path}${query ? `?${query}` : ''}`;
}

function pick(result) {
    return {
        data: result.data?.data ?? null,
        origin: result.data?.origin ?? null,
        unavailable: result.data?.unavailable ?? null,
        isLoading: result.isLoading,
        isFetching: result.isFetching,
        dataUpdatedAt: result.dataUpdatedAt,
        error: result.error,
        refetch: result.refetch,
    };
}

// ---- la bandeja ----

export function useAttention({ kind = '', offset = 0 } = {}) {
    const params = { kind: kind || undefined, limit: PAGE_SIZE, offset };
    const key = coordinationKeys.attention(SOURCE, params);

    const result = useQuery(
        onFixtures
            ? fixtureQuery(
                  key,
                  kind === FIXTURE_SCOPE.attentionKind ? 'attentionKind' : 'attention',
                  () =>
                      kind && kind !== FIXTURE_SCOPE.attentionKind
                          ? `Los fixtures traen una sola muestra filtrada, la de «${FIXTURE_SCOPE.attentionKind}». Los totales de cada clase sí salen de \`by_kind\`, que viaja completo en la muestra sin filtrar.`
                          : null,
              )
            : apiQuery(key, coordinationPaths.attention(), params),
    );

    return pick(result);
}

// ---- la parrilla ----

export function useSeats({ offset = 0, repositoryId } = {}) {
    const params = { limit: PAGE_SIZE, offset, repository_id: repositoryId || undefined };
    const key = coordinationKeys.seats(SOURCE, params);
    return pick(
        useQuery(
            onFixtures
                ? fixtureQuery(key, 'seats')
                : apiQuery(key, coordinationPaths.seats(), params),
        ),
    );
}

export function useSeat(id) {
    const { can } = useAuth();
    const key = coordinationKeys.seat(SOURCE, id);
    const result = useQuery({
        ...(onFixtures
            ? fixtureQuery(key, 'seat', () =>
                  String(id) !== String(FIXTURE_SCOPE.seatId)
                      ? `El fixture trae el detalle del puesto ${FIXTURE_SCOPE.seatId} y de ninguno más. Contra un Core real, esta misma pantalla lee \`GET /api/v1/admin/coordination/seats/${id}\`.`
                      : null,
              )
            : apiQuery(key, coordinationPaths.seat(id))),
        enabled: id != null && can('machines:read'),
    });
    return pick(result);
}

export function useControl(positionId) {
    const key = coordinationKeys.control(SOURCE, positionId);
    const result = useQuery({
        ...(onFixtures
            ? fixtureQuery(key, 'control', () =>
                  String(positionId) !== String(FIXTURE_SCOPE.controlPositionId)
                      ? `El fixture trae el estado deseado del puesto ${FIXTURE_SCOPE.controlPositionId} y de ninguno más.`
                      : null,
              )
            : apiQuery(key, coordinationPaths.control(positionId))),
        enabled: positionId != null,
    });
    return pick(result);
}

// ---- el historial ----

export function useHistory({ after = '0', family = '' } = {}) {
    const params = { after: after || undefined, family: family || undefined, limit: PAGE_SIZE };
    const key = coordinationKeys.history(SOURCE, params);
    return pick(
        useQuery(
            onFixtures
                ? fixtureQuery(key, 'history')
                : apiQuery(key, coordinationPaths.history(), params),
        ),
    );
}

export function useSnapshot() {
    const key = coordinationKeys.snapshot(SOURCE);
    return pick(
        useQuery(
            onFixtures
                ? fixtureQuery(key, 'snapshot')
                : apiQuery(key, coordinationPaths.snapshot()),
        ),
    );
}

// ---- los tiempos ----

export function useTaskMetrics(taskId) {
    const key = coordinationKeys.taskMetrics(SOURCE, taskId);
    const result = useQuery({
        ...(onFixtures
            ? fixtureQuery(key, 'taskMetrics', () =>
                  String(taskId) !== String(FIXTURE_SCOPE.taskId)
                      ? `El fixture trae el desglose de la tarea ${FIXTURE_SCOPE.taskId} y de ninguna otra.`
                      : null,
              )
            : apiQuery(key, coordinationPaths.taskMetrics(taskId))),
        enabled: taskId != null,
    });
    return pick(result);
}

export function useDurations(filters = {}) {
    const key = coordinationKeys.durations(SOURCE, filters);
    return pick(
        useQuery(
            onFixtures
                ? fixtureQuery(key, 'durations')
                : apiQuery(key, coordinationPaths.durations(), filters),
        ),
    );
}

// ---- las escrituras ----
//
// ON FIXTURES THEY DO NOT TRAVEL. The demo composes the exact body the contract
// asks for and shows it; it does not pretend the order was issued, because a
// screen that says «pausado» when nothing was written is the one lie this whole
// surface exists to avoid. With `VITE_COORDINATION_SOURCE=api` the same call
// goes out for real, with the session's CSRF header (core/network/api.js).

export function useIssueOrder(positionId) {
    const client = useQueryClient();
    return useMutation({
        onSuccess: () => client.invalidateQueries({ queryKey: coordinationKeys.all }),
        mutationFn: async body => {
            const line = `POST /api/v1/${coordinationPaths.orders(positionId)}`;
            if (onFixtures) return { sent: false, request: line, body };
            const response = await api.post(coordinationPaths.orders(positionId), body);
            return { sent: true, request: line, body, response: response.data };
        },
    });
}

export function useDecideRound(taskId) {
    const client = useQueryClient();
    return useMutation({
        onSuccess: () => client.invalidateQueries({ queryKey: coordinationKeys.all }),
        mutationFn: async body => {
            const line = `POST /api/v1/${coordinationPaths.roundDecision(taskId)}`;
            if (onFixtures) return { sent: false, request: line, body };
            const response = await api.post(coordinationPaths.roundDecision(taskId), body);
            return { sent: true, request: line, body, response: response.data };
        },
    });
}

export function useMetricTasks({ projectId, repositoryId, offset = 0, enabled = true } = {}) {
    const { can } = useAuth();
    const params = {
        project_id: projectId || undefined,
        repository_id: repositoryId || undefined,
        modality: 'all',
        limit: PAGE_SIZE,
        offset,
    };
    return useQuery({
        queryKey: [...coordinationKeys.all, 'task-picker', params],
        queryFn: ({ signal }) => api.get('admin/tasks', { params, signal }).then(r => r.data),
        enabled: enabled && !onFixtures && can('tasks:read'),
    });
}
